import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useId, useState } from 'react'

import Header, { HeaderSpacer } from '@src/app/Header'
import type { Plan } from '@src/app/billing/pricing/PricingController'
import { useAppStore } from '@src/app/context'
import Logo from '@src/component/logo/Logo'
import config from '@src/config'
import useStatefulPromise from '@src/lib/hooks/useStatefulPromise'
import Avatar from '@ui/Avatar/Avatar'
import Button from '@ui/Button/Button'
import { Icon } from '@ui/Icon/Icon'
import { LoadingIndicator } from '@ui/LoadingIndicator'
import Typography from '@ui/Typography'
import { HStack, VStack } from '@ui/layout/Stack'

import * as styles from './ThirdPartyAuthorize.css'
import { scopeList } from './scopes'

import EmptyState from '@odyssey/components/EmptyState/EmptyState'

interface Application {
  clientId: string
  description: string
  id: string
  logoUrl: string
  name: string
  verified: boolean
}

interface OAuthParams {
  scope: string
  client_id: string
  redirect_uri: string
  response_type: string
  state: string
}

function isOAuthParams(params: any): params is OAuthParams {
  return (
    !!params &&
    typeof params === 'object' &&
    'scope' in params &&
    'client_id' in params &&
    'redirect_uri' in params &&
    'response_type' in params
  )
}

function getWorkspaceSummary(
  subscriptionType: string | null | undefined,
  memberCount: number,
  getPlanName: (plan: Plan) => string,
): string {
  const plan =
    subscriptionType === 'standard' ||
    subscriptionType === 'premium' ||
    subscriptionType === 'scale'
      ? `${getPlanName(subscriptionType)} plan`
      : null
  const members =
    memberCount > 0
      ? `${memberCount.toLocaleString()} ${memberCount === 1 ? 'member' : 'members'}`
      : null

  return [plan, members].filter(Boolean).join(' · ')
}

function ThirdPartyAuthorize() {
  const { service, history, billing } = useAppStore()
  const [oauthParams, setOAuthParams] = useState<OAuthParams | null>(null)
  const org = service.organization.current
  const workspaceSummary = getWorkspaceSummary(
    service.billing.subscription?.type,
    service.member.collection.length,
    (plan) => billing.getPlanName(plan),
  )
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [authTokenError, setAuthTokenError] = useState<Error | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [appResponse, findApp] = useStatefulPromise<Application>(() => {
    const params = history.query

    if (isOAuthParams(params)) {
      setOAuthParams({
        scope: params.scope,
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        response_type: params.response_type,
        state: params.state,
      })

      return service.integration.getApplication(params.client_id, params.redirect_uri)
    }

    return Promise.reject(new Error('Could not find the application'))
  })

  const resolveAuthToken = useCallback(async () => {
    if (authToken) {
      return
    }

    try {
      setAuthToken(await service.authV2.getAccessToken())
    } catch {
      setAuthTokenError(new Error('Could not verify your session'))
    }
  }, [authToken, service.authV2])

  useEffect(() => {
    if (appResponse.status !== 'idle') {
      return
    }

    void findApp()
  }, [appResponse.status, findApp])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: Derive state or move to event handler (UXF-1123)
    void resolveAuthToken()
  }, [resolveAuthToken])

  const handleBack = () => {
    history.goBack()
  }

  const handleCancel = () => {
    history.push('/')
  }

  const handleSubmit = () => {
    setIsSubmitting(true)
  }

  const scopesHeaderId = useId()
  const error = appResponse.error ?? authTokenError

  return (
    <div className={styles.root}>
      <Header className={styles.header}>
        <Logo />
        <HeaderSpacer />
      </Header>
      <main className={styles.main}>
        <div className={styles.card}>
          {error ? (
            <EmptyState
              className={styles.error}
              icon={<Icon icon="std.warning--20" className={styles.errorIcon} />}
              title="Something went wrong"
              description={error.message}
              action={
                <Button variant="ghost" color="subtle" onClick={handleBack}>
                  Go back
                </Button>
              }
            />
          ) : appResponse.data && oauthParams ? (
            <form
              method="post"
              action={`${config.AUTH_SERVICE_URL}oauth`}
              onSubmit={handleSubmit}
              aria-busy={isSubmitting}
            >
              {(Object.keys(oauthParams) as (keyof typeof oauthParams)[]).map((key) => (
                <input type="hidden" key={key} name={key} value={oauthParams[key]} />
              ))}
              <input type="hidden" name="grant_type" value="authorization_code" />
              <input type="hidden" name="authorization" value={authToken ?? ''} />

              <VStack gap={24} alignItems="stretch">
                <VStack gap={14}>
                  <Avatar
                    variant="workspace"
                    size={36}
                    circleClassName={styles.appLogo}
                    url={appResponse.data.logoUrl || undefined}
                    label={`${appResponse.data.name} logo`}
                  />
                  <VStack gap={6} alignItems="stretch" className={styles.introText}>
                    <Typography component="h1" variant="title2">
                      Connect with {appResponse.data.name}
                    </Typography>
                    <Typography component="p" variant="body" color="textSecondary">
                      Grant{' '}
                      <Typography
                        component="strong"
                        variant="inherit"
                        fontWeight="semibold"
                      >
                        {appResponse.data.name}
                      </Typography>{' '}
                      access to your workspace
                    </Typography>
                  </VStack>
                </VStack>

                {org?.name ? (
                  <HStack gap={12} className={styles.workspace}>
                    <Avatar
                      variant="workspace"
                      size={40}
                      circleClassName={styles.workspaceAvatar}
                      url={org.pictureUrl ?? undefined}
                      label={`${org.name} workspace avatar`}
                    />
                    <VStack gap={2} alignItems="stretch" className={styles.workspaceName}>
                      <Typography component="p" variant="callout" nowrap>
                        {org.name}
                      </Typography>
                      {workspaceSummary ? (
                        <Typography
                          component="p"
                          variant="footnote"
                          color="textTertiary"
                          nowrap
                        >
                          {workspaceSummary}
                        </Typography>
                      ) : null}
                    </VStack>
                  </HStack>
                ) : null}

                <VStack gap={14} alignItems="stretch" className={styles.scopes}>
                  <Typography
                    component="p"
                    variant="callout"
                    fontWeight="medium"
                    id={scopesHeaderId}
                  >
                    {appResponse.data.name} will be able to:
                  </Typography>
                  <VStack
                    component="ul"
                    gap={14}
                    alignItems="stretch"
                    aria-labelledby={scopesHeaderId}
                    className={styles.scopesList}
                  >
                    {scopeList(oauthParams.scope).map((description) => (
                      <HStack
                        component="li"
                        gap={10}
                        alignItems="flex-start"
                        className={styles.scope}
                        key={description}
                      >
                        <Icon
                          icon="std.mark-done--20"
                          className={styles.scopeIcon}
                          aria-hidden
                        />
                        <Typography
                          component="span"
                          variant="callout"
                          color="textSecondary"
                        >
                          {description}
                        </Typography>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>

                <VStack gap={10} alignItems="stretch">
                  <Button
                    type="submit"
                    fullWidth
                    loading={isSubmitting}
                    disabled={!authToken}
                  >
                    Allow
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    color="subtle"
                    fullWidth
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </VStack>
              </VStack>
            </form>
          ) : (
            <HStack justifyContent="center" className={styles.loading}>
              <LoadingIndicator variant="circular" size={20} color="gray" />
            </HStack>
          )}
        </div>
      </main>
    </div>
  )
}

export default observer(ThirdPartyAuthorize)
